import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslator } from 'tradera-lang/translate';
import AttributeButton from 'tradera-components/item-card/item-card-parts/attribute-button';
import AttributeColor from 'tradera-components/attributes/attribute-color';
import AttributeModal from 'tradera-components/attributes/attribute-modal';
import {
    getTranslatedAttributeName,
    addText,
    addTextAndReSort
} from 'tradera-components/attributes/attribute-helper';

const numberToShow = 17;

export const AttributeFilter = ({
    attributeFilter,
    handleAttributeFilterChange,
    queryParams
}) => {
    if (!attributeFilter) {
        return null;
    }

    const {
        options,
        translateOptions,
        parameter,
        presentation,
        selectedValues,
        preserveOptionsOrder
    } = attributeFilter;

    return (
        <div className="attribute-filter">
            <AttributeOptionsShortList
                options={options}
                parameter={parameter}
                presentation={presentation}
                selectedValues={selectedValues}
                translateOptions={translateOptions}
                handleAttributeFilterChange={handleAttributeFilterChange}
                queryParams={queryParams}
                preserveOptionsOrder={preserveOptionsOrder}
            />
            <div>
                {options.length > numberToShow && (
                    <AllAttributeOptionsModalButton
                        options={options}
                        parameter={parameter}
                        defaultSelected={selectedValues}
                        selectAction={handleAttributeFilterChange}
                        translateOptions={translateOptions}
                    />
                )}
            </div>
        </div>
    );
};

const AttributeOptionsShortList = ({
    options,
    parameter,
    presentation,
    selectedValues,
    translateOptions,
    handleAttributeFilterChange,
    preserveOptionsOrder
}) => {
    const { t } = useTranslator();
    const optionsWithText = preserveOptionsOrder
        ? addText(parameter, t, translateOptions, options)
        : addTextAndReSort(parameter, t, translateOptions, options);
    return optionsWithText.map((optionWithText, index) => {
        const selected = selectedValues.includes(optionWithText.value);
        const hideAttribute = index > numberToShow && !selected;
        if (presentation === 'color') {
            return (
                <AttributeColor
                    key={index}
                    optionWithText={optionWithText}
                    hideAttribute={hideAttribute}
                    handleClick={() => {
                        handleAttributeFilterChange(
                            parameter,
                            optionWithText.value
                        );
                    }}
                    selected={selected}
                />
            );
        } else {
            return (
                <AttributeButton
                    key={index}
                    text={optionWithText.text}
                    className={`${
                        hideAttribute ? 'd-none' : 'd-inline-block'
                    } mw-100`}
                    onClick={() => {
                        handleAttributeFilterChange(
                            parameter,
                            optionWithText.value
                        );
                    }}
                    selected={selected}
                    hoverIcon={false}
                />
            );
        }
    });
};

AttributeFilter.propTypes = {
    attributeFilter: PropTypes.shape({
        name: PropTypes.string,
        options: PropTypes.array,
        parameter: PropTypes.string,
        selectedValues: PropTypes.array,
        type: PropTypes.string
    }).isRequired,
    handleAttributeFilterChange: PropTypes.func,
    queryParams: PropTypes.object.isRequired
};

const AllAttributeOptionsModalButton = ({
    options,
    defaultSelected,
    parameter,
    selectAction,
    translateOptions
}) => {
    const { t } = useTranslator();
    const [modalOpen, openModal] = useState(false);
    return (
        <>
            <button
                className="btn btn-md-sm btn-outline-dark w-100 my-2"
                onClick={() => openModal(true)}>
                {t('searchResults_attributesFilter_showMore')}
            </button>

            {modalOpen && (
                <AttributeModal
                    title={getTranslatedAttributeName(parameter, t)}
                    optionsWithText={addTextAndReSort(
                        parameter,
                        t,
                        translateOptions,
                        options
                    )}
                    defaultSelected={defaultSelected}
                    selectAction={value => {
                        selectAction(parameter, value);
                    }}
                    onClose={() => openModal(false)}
                    parameter={parameter}
                />
            )}
        </>
    );
};
